import * as React from "react";

import {
  Badge,
  Box,
  BoxProps,
  Button,
  Flex,
  HStack,
  Icon,
  IconButton,
  Image,
  Text,
  Tooltip,
  VStack,
  chakra,
  useDisclosure,
} from "@chakra-ui/react";
import { Avatar, Card, Loading, StaffAvatar } from "shared";
import {
  ChartRow,
  ChartRowElement,
  ChartRowGroup,
  ChartFormSubsectionPayload,
  ChartSubsection,
  ChartSubsectionContent,
  ChartSubsectionHeader,
  OrderChartSectionHeader,
  mapAnswersListToFormObject,
  mapFormObjectToAnswersList,
  renderOrderItemTime,
  useSaveAndNext,
} from "modules/charts-shared";

import {
  EmrBed,
  EmrCollapse,
  EmrEditCircle,
  EmrExpand,
  EmrForm,
  EmrInProcess,
  EmrLabs,
  EmrNotes,
  EmrProfileComplete,
  EmrRegistration,
  EmrSignature,
  SiChevronRight,
  SiTask,
} from "@medstonetech/slate-icons";

import {
  FormProvider,
  SubmitHandler,
  UseFormReturn,
  useForm,
  useWatch,
} from "react-hook-form";
import { ORDER_STATUS_COLOR, OrderHeaderCard } from "modules/orders";
import {
  PdfViewer,
  PdfViewerActions,
  PdfViewerActivePage,
  PdfViewerContainer,
  PdfViewerHeader,
  PdfViewerPageIndicator,
  PdfViewerPreview,
} from "shared/pdf-viewer";
import {
  UseOrderChartResponseItem,
  useOrderChart,
  useUpdateOrderChart,
} from "modules/provider/api";
import {
  enumMapper,
  extractApiErrorMessage,
  isAuditorTeamTypes,
  useEffectOnce,
} from "utils";
import { useLocation, useParams } from "react-router-dom";

import { BaseSharedChartProps } from "modules/provider/types";
import { ChartForm } from "modules/charts-shared-v2";
import { ChartRouteBaseParams } from "modules/charts-shared/types";
import { FormPromptWithSaveProps } from "shared/prompt";
import {
  LABS_DATE_TIME_QCODE,
  LABS_GRID_COLUMNS,
  LABS_SECTION_CODE,
} from "./constants";
import { LabsChartItem } from "types/chart/labs-chart";
import { OrderStatus } from "enums";
import { USERID_CLAIMS } from "system-constants";
import { UserType } from "modules/identity";
import { formMessages } from "messages";
import { useAuth0 } from "@auth0/auth0-react";
import { useEncounterOrderFile } from "modules/orders/api";
import { useGetUserDetails } from "api";
import { useGetUserSignature } from "modules/onboarding/api";
import { useHandlerBeforeSessionTimeout, useToast } from "hooks";
import { useUserRoles } from "contexts/UserRoles";
import { useChartTrackingContext } from "contexts";
import { ModalOrderNotes } from "../ModalOrderNotes";
import { hasLabInterpretation } from "./utils";

const LABS_SUBSECTIONS = [
  "Q001",
  "Q002",
  "Q003",
  "Q004",
  "Q005",
  "Q006",
  "Q007",
  "Q008",
] as const;

const LABS_NON_ANSWER_FIELDS = new Set([
  "orderId",
  "orderNumber",
  "order",
  "userId",
  "signedBy",
  "interactions",
]);

function mapLabsAnswerKeyToSubsection(
  answerKey: string
): typeof LABS_SUBSECTIONS[number] {
  if (
    answerKey.startsWith("wbc") ||
    answerKey.startsWith("hgb") ||
    answerKey.startsWith("hct") ||
    answerKey.startsWith("platelets") ||
    answerKey.startsWith("segs") ||
    answerKey.startsWith("bands") ||
    answerKey.startsWith("lfts")
  ) {
    return "Q001";
  }

  if (
    answerKey.startsWith("chemistries") ||
    answerKey === "na" ||
    answerKey.startsWith("na") ||
    answerKey.startsWith("k") ||
    answerKey.startsWith("co2") ||
    answerKey.startsWith("gluc") ||
    answerKey.startsWith("bun") ||
    answerKey.startsWith("creat") ||
    answerKey.startsWith("ck") ||
    answerKey.startsWith("troponin") ||
    answerKey.startsWith("ptptt") ||
    answerKey.startsWith("inr") ||
    answerKey.startsWith("amylase") ||
    answerKey.startsWith("lipase")
  ) {
    return "Q002";
  }

  if (answerKey.startsWith("ua") || answerKey.startsWith("lacticAcid")) {
    return "Q003";
  }

  if (answerKey.startsWith("abg") || answerKey.startsWith("ababgloodX")) {
    return "Q005";
  }

  if (answerKey.startsWith("cultures") || answerKey.startsWith("cs")) {
    return "Q006";
  }

  if (answerKey.startsWith("peakFlow")) {
    return "Q007";
  }

  if (answerKey.startsWith("pulseOx")) {
    return "Q008";
  }

  return "Q004";
}

type LabsInteractionSnapshotItem = Record<string, unknown> & {
  orderId: string;
};

function mapLabsItemToSubsectionPayload(item: LabsInteractionSnapshotItem) {
  const subsectionAnswersMap = LABS_SUBSECTIONS.reduce<
    Record<string, Record<string, unknown>>
  >(
    (acc, subsectionId) => ({
      ...acc,
      [subsectionId]: {},
    }),
    {}
  );

  Object.entries(item).forEach(([answerKey, answerValue]) => {
    if (LABS_NON_ANSWER_FIELDS.has(answerKey)) {
      return;
    }

    const subsectionId = mapLabsAnswerKeyToSubsection(answerKey);
    subsectionAnswersMap[subsectionId][answerKey] = answerValue;
  });

  return LABS_SUBSECTIONS.map<ChartFormSubsectionPayload>((subsectionId) => ({
    code: `${item.orderId}_${subsectionId}`,
    answers: mapFormObjectToAnswersList(subsectionAnswersMap[subsectionId]),
  }));
}

function mapLabsItemsToSubsectionPayload(items: LabsInteractionSnapshotItem[]) {
  return items.flatMap((item) => mapLabsItemToSubsectionPayload(item));
}

const GRID_PROPS: BoxProps = {
  padding: "1rem 1.5rem",
  textAlign: "center",
  display: "grid",
  gridTemplateColumns: "5fr 12fr 12fr 10fr 10fr 25fr 25fr 4fr 4fr 8fr 3fr",
  gridColumnGap: "1rem",
  alignItems: "center",
};

type LabsChartForm = {
  items: LabsChartItem[];
};

type LabsChartFileProps = {
  file: Blob | undefined;
  orderNumber: number;
  isFileMode: boolean;
  toggleFileMode: () => void;
};

function LabsChartFile({
  file,
  orderNumber,
  isFileMode,
  toggleFileMode,
}: LabsChartFileProps) {
  return (
    <PdfViewer value={file}>
      <PdfViewerContainer
        height={isFileMode ? "160%" : "90%"}
        marginBottom="29px"
        activePage={<PdfViewerActivePage pageWidth={isFileMode ? 1600 : 900} />}
        header={
          <PdfViewerHeader
            title={`Lab Report ${!!orderNumber ? `# ${orderNumber}` : ""}`}
            leftElements={[
              <IconButton
                key="expand-collapse-button"
                aria-label="submit document"
                icon={<Icon as={isFileMode ? EmrCollapse : EmrExpand} />}
                color="blue"
                variant="icon"
                onClick={() => toggleFileMode()}
              />,
              <PdfViewerPageIndicator key="page-indicator" />,
            ]}
            rightElements={[
              <PdfViewerActions
                key="viewer-actions"
                showSubmit={false}
                showDelete={false}
              />,
            ]}
          />
        }
        preview={<PdfViewerPreview />}
      />
    </PdfViewer>
  );
}

type LabsCountersProps = {
  filter: OrderStatus | null;
  setFilter: (filter: OrderStatus | null) => void;
  useFormContext: UseFormReturn<LabsChartForm>;
};

function LabsCounters({
  filter,
  setFilter,
  useFormContext,
}: LabsCountersProps) {
  const OrderHeaderCardComponent = OrderHeaderCard as React.ComponentType<
    Record<string, unknown>
  >;

  const { control } = useFormContext;

  const subsections = useWatch({ control, name: "items" });

  const handleSetFilter = React.useCallback(
    (newFilter: OrderStatus) => {
      if (filter === newFilter) {
        setFilter(null);
      } else {
        setFilter(newFilter);
      }
    },
    [filter, setFilter]
  );

  const getCounter = React.useCallback(
    (status: string) => {
      let counter = 0;
      if (subsections) {
        counter = subsections?.filter(
          (subsection) => subsection?.order.status === status
        ).length;
      }
      return counter;
    },
    [subsections]
  );

  const orderedBg: string =
    filter === "Ordered" || filter === null ? "orange" : "white";
  const orderedColor: string =
    filter === "Ordered" || filter === null ? "white" : "gray";

  const inProcessBg: string =
    filter === "InProcess" || filter === null ? "blue" : "white";
  const inProcessColor: string =
    filter === "InProcess" || filter === null ? "white" : "gray";

  const completedBg: string =
    filter === "Completed" || filter === null ? "green" : "white";
  const completedColor: string =
    filter === "Completed" || filter === null ? "white" : "gray";

  return (
    <Flex columnGap="30px">
      <OrderHeaderCardComponent
        title="Ordered"
        icon={<Icon as={EmrRegistration} />}
        bg={orderedBg}
        color={orderedColor}
        cursor="pointer"
        onClick={() => handleSetFilter("Ordered")}
        counter={getCounter("Ordered")}
        flex={1}
      />
      <OrderHeaderCardComponent
        title="In Process"
        icon={<Icon as={EmrInProcess} />}
        bg={inProcessBg}
        color={inProcessColor}
        cursor="pointer"
        onClick={() => handleSetFilter("InProcess")}
        counter={getCounter("InProcess")}
        flex={1}
      />
      <OrderHeaderCardComponent
        title="Completed"
        icon={<Icon as={SiTask} />}
        bg={completedBg}
        color={completedColor}
        cursor="pointer"
        onClick={() => handleSetFilter("Completed")}
        counter={getCounter("Completed")}
        flex={1}
      />
    </Flex>
  );
}

function LabsHeaders() {
  return (
    <Card
      bg="gray.250"
      marginBottom="1rem"
      boxShadow="none"
      {...GRID_PROPS}
      columnGap="0.5rem"
    >
      <chakra.span>
        <Icon
          as={EmrProfileComplete}
          w="1.25rem"
          h="1.25rem"
          color="gray.650"
        />
      </chakra.span>
      <chakra.span>Date / Time</chakra.span>
      <chakra.span>Order ID</chakra.span>
      <chakra.span>Scheduled</chakra.span>
      <chakra.span textAlign="center">
        Observation <br /> Order
      </chakra.span>
      <chakra.span textAlign="center">Order</chakra.span>
      <chakra.span textAlign="left">Order Notes</chakra.span>
      <chakra.span>Report</chakra.span>
      <chakra.span>Interpretation</chakra.span>
      <chakra.span>Status</chakra.span>
      <chakra.span></chakra.span>
    </Card>
  );
}

type LabItemProps = {
  item: UseOrderChartResponseItem;
  index: number;
  encounterId: string;
  chartCode: string;
  isReadOnly?: boolean;
  useFormContext: UseFormReturn<LabsChartForm>;
  hideInterpretations?: boolean;
};

type LabItemSignatureProps = Pick<LabItemProps, "index" | "useFormContext"> & {
  isReadOnly?: boolean;
};

const LabItemSignature = React.memo(function LabItemSignature(
  props: LabItemSignatureProps
) {
  const { index, useFormContext, isReadOnly } = props;

  const { control, setValue } = useFormContext;

  const signedBy = useWatch({ control, name: `items.${index}.signedBy` });

  const { data: signature } = useGetUserSignature(signedBy || "");

  const { user } = useAuth0();

  const handleSign = React.useCallback(
    () =>
      setValue(`items.${index}.signedBy`, user?.[USERID_CLAIMS], {
        shouldDirty: true,
      }),
    [setValue, index, user]
  );

  const { roles } = useUserRoles();

  const isAllowedToSign = React.useMemo(() => {
    const rolesAllowedToViewOpenChartCounter: UserType[] = [
      "Administrator",
      "SupervisingProvider",
      "Provider",
    ];

    return roles.some((x) => rolesAllowedToViewOpenChartCounter.includes(x));
  }, [roles]);

  return (
    <Box height="100px" p={6} width="100%" bgColor="white" borderRadius={20}>
      <HStack width="100%" spacing={6}>
        <HStack display="flex" justifyContent="start" spacing={4} flex={2}>
          <Text fontWeight="600" fontSize="18px">
            Provider Signature
          </Text>
        </HStack>
        {!!signedBy && signature?.data?.fullName ? (
          <Flex justifyContent="center" flex={1}>
            <HStack>
              <StaffAvatar
                w="40px"
                h="40px"
                size="xs"
                fontSize=".75rem"
                userName={signature.data.fullName}
                profileUrl={signature?.data?.pictureUrl}
              />
              <VStack spacing="0" alignItems="flex-start">
                <Box
                  fontSize="1.0625rem"
                  fontWeight="600"
                  lineHeight="1.5rem"
                  color="black"
                >
                  {signature.data.fullName}
                </Box>
                <Box
                  fontSize="0.875rem"
                  fontWeight="400"
                  lineHeight="1rem"
                  color="gray.700"
                >
                  {signature?.data?.teams}
                </Box>
              </VStack>
            </HStack>
          </Flex>
        ) : (
          <Text>No signature added</Text>
        )}
        <HStack
          pl={4}
          display="flex"
          justifyContent="center"
          alignItems="center"
          spacing={40}
          flex={2}
        >
          <HStack spacing={10}>
            {!!signedBy ? (
              <>
                <Text fontWeight="600" fontSize="16px" color="gray.700">
                  E-signed by:
                </Text>
                <Box minW={300} maxW={400} minH={50}>
                  <Image
                    src={signature?.data?.signature}
                    height="100%"
                    width="100%"
                  />
                </Box>
              </>
            ) : location.pathname.includes("triage") ? (
              <Button
                variant="label"
                color="blue"
                onClick={handleSign}
                disabled={!isAllowedToSign || isReadOnly}
              >
                <Icon as={EmrSignature} fontSize="1.5rem" color="blue" m={2} />
                Add Signature
              </Button>
            ) : (
              <Tooltip
                isDisabled={isAllowedToSign}
                label="Only Providers are allowed to sign"
              >
                <Button
                  variant="label"
                  color="blue"
                  onClick={handleSign}
                  disabled={!isAllowedToSign || isReadOnly}
                >
                  <Icon
                    as={EmrSignature}
                    fontSize="1.5rem"
                    color="blue"
                    m={2}
                  />
                  Add Signature
                </Button>
              </Tooltip>
            )}
          </HStack>
        </HStack>
      </HStack>
    </Box>
  );
});

const LabItem = React.memo(function LabItem({
  chartCode,
  encounterId,
  item,
  index,
  isReadOnly,
  useFormContext,
  hideInterpretations = false,
}: LabItemProps) {
  const location = useLocation();
  const { user: currentUser } = useAuth0();

  const fromSummary = location.pathname.includes("medical-records");

  const [isExpanded, setIsExpanded] = React.useState(fromSummary);
  const [isFileMode, setFileMode] = React.useState(false);

  const { isOpen, onToggle } = useDisclosure();

  const { control, setValue, watch } = useFormContext;

  const toggleExpand = React.useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  const userId = useWatch({ control, name: `items.${index}.userId` });
  const { data: userDetails } = useGetUserDetails(userId as string);
  const isAuditorModifiedBy = isAuditorTeamTypes(userDetails?.data?.teamTypes);

  const handleChangeUpdatedBy = React.useCallback(() => {
    setValue(`items.${index}.userId`, currentUser?.[USERID_CLAIMS], {
      shouldDirty: true,
    });
  }, [currentUser, index, setValue]);

  const hasInterpretation = React.useMemo(
    () => hasLabInterpretation(item.answers),
    [item.answers]
  );

  const getSubsectionInteractions = React.useCallback(
    (subsectionId: string) =>
      (item.interactions || []).filter(
        (interaction) => interaction.code === `${item.orderId}_${subsectionId}`
      ),
    [item.interactions, item.orderId]
  );

  const { appendChartElements } = useChartTrackingContext();

  useEffectOnce(() => {
    const p = `items[${index}]`;
    appendChartElements([
      // Q001 - CBC
      { name: `${p}.wbc`, label: "WBC", type: "cross-option" },
      { name: `${p}.wbcNotes`, label: "WBC Notes", type: "input" },
      { name: `${p}.hgb`, label: "Hgb", type: "cross-option" },
      { name: `${p}.hgbNotes`, label: "Hgb Notes", type: "input" },
      { name: `${p}.hct`, label: "Hct", type: "cross-option" },
      { name: `${p}.hctNotes`, label: "Hct Notes", type: "input" },
      { name: `${p}.platelets`, label: "Platelets", type: "cross-option" },
      { name: `${p}.plateletsNotes`, label: "Platelets Notes", type: "input" },
      { name: `${p}.segs`, label: "segs", type: "cross-option" },
      { name: `${p}.segsNotes`, label: "segs Notes", type: "input" },
      { name: `${p}.bands`, label: "bands", type: "cross-option" },
      { name: `${p}.bandsNotes`, label: "bands Notes", type: "input" },
      { name: `${p}.lymphs`, label: "lymphs", type: "cross-option" },
      { name: `${p}.lymphsNotes`, label: "lymphs Notes", type: "input" },
      { name: `${p}.cbcExtraOne`, label: "CBC Extra 1", type: "input" },
      {
        name: `${p}.cbcExtraOneNotes`,
        label: "CBC Extra 1 Notes",
        type: "input",
      },
      { name: `${p}.cbcExtraTwo`, label: "CBC Extra 2", type: "input" },
      {
        name: `${p}.cbcExtraTwoNotes`,
        label: "CBC Extra 2 Notes",
        type: "input",
      },
      { name: `${p}.wbcSwitch`, label: "CBC Switch", type: "switch" },
      {
        name: `${p}.wbcAllNormal`,
        label: "CBC All Normal",
        type: "checkbox-button",
      },
      { name: `${p}.wbcExcept`, label: "CBC Except", type: "checkbox-button" },
      // Q002 - Chemistries
      { name: `${p}.na`, label: "NA", type: "cross-option" },
      { name: `${p}.naNotes`, label: "NA Notes", type: "input" },
      { name: `${p}.k`, label: "K", type: "cross-option" },
      { name: `${p}.kNotes`, label: "K Notes", type: "input" },
      { name: `${p}.co2`, label: "CO2", type: "cross-option" },
      { name: `${p}.co2Notes`, label: "CO2 Notes", type: "input" },
      { name: `${p}.gluc`, label: "Gluc", type: "cross-option" },
      { name: `${p}.glucNotes`, label: "Gluc Notes", type: "input" },
      { name: `${p}.bun`, label: "Bun", type: "cross-option" },
      { name: `${p}.bunNotes`, label: "Bun Notes", type: "input" },
      { name: `${p}.creat`, label: "Creat", type: "cross-option" },
      { name: `${p}.creatNotes`, label: "Creat Notes", type: "input" },
      {
        name: `${p}.chemistriesExtraOne`,
        label: "Chemistries Extra 1",
        type: "input",
      },
      {
        name: `${p}.chemistriesExtraOneNotes`,
        label: "Chemistries Extra 1 Notes",
        type: "input",
      },
      {
        name: `${p}.chemistriesExtraTwo`,
        label: "Chemistries Extra 2",
        type: "input",
      },
      {
        name: `${p}.chemistriesExtraTwoNotes`,
        label: "Chemistries Extra 2 Notes",
        type: "input",
      },
      {
        name: `${p}.chemistriesSwitch`,
        label: "Chemistries Switch",
        type: "switch",
      },
      {
        name: `${p}.chemistriesAllNormal`,
        label: "Chemistries All Normal",
        type: "checkbox-button",
      },
      {
        name: `${p}.chemistriesExcept`,
        label: "Chemistries Except",
        type: "checkbox-button",
      },
      // Q003 - UA
      { name: `${p}.uaWbc`, label: "UA WBC", type: "cross-option" },
      { name: `${p}.uaWbcNotes`, label: "UA WBC Notes", type: "input" },
      { name: `${p}.uaRbc`, label: "UA RBC", type: "cross-option" },
      { name: `${p}.uaRbcNotes`, label: "UA RBC Notes", type: "input" },
      { name: `${p}.uaBacteria`, label: "UA bacteria", type: "cross-option" },
      {
        name: `${p}.uaBacteriaNotes`,
        label: "UA bacteria Notes",
        type: "input",
      },
      { name: `${p}.uaDip`, label: "UA dip", type: "cross-option" },
      { name: `${p}.uaDipNotes`, label: "UA dip Notes", type: "input" },
      { name: `${p}.uaExtraOne`, label: "UA Extra 1", type: "input" },
      {
        name: `${p}.uaExtraOneNotes`,
        label: "UA Extra 1 Notes",
        type: "input",
      },
      { name: `${p}.uaExtraTwo`, label: "UA Extra 2", type: "input" },
      {
        name: `${p}.uaExtraTwoNotes`,
        label: "UA Extra 2 Notes",
        type: "input",
      },
      { name: `${p}.uaSwitch`, label: "UA Switch", type: "switch" },
      {
        name: `${p}.uaAllNormal`,
        label: "UA All Normal",
        type: "checkbox-button",
      },
      { name: `${p}.uaExcept`, label: "UA Except", type: "checkbox-button" },
      // Q004 - Other
      {
        name: `${p}.acetaminophen`,
        label: "acetaminophen",
        type: "cross-option",
      },
      {
        name: `${p}.acetaminophenNotes`,
        label: "acetaminophen Notes",
        type: "input",
      },
      { name: `${p}.alkPhos`, label: "Alk Phos", type: "cross-option" },
      { name: `${p}.alkPhosNotes`, label: "Alk Phos Notes", type: "input" },
      { name: `${p}.ammonia`, label: "ammonia", type: "cross-option" },
      { name: `${p}.ammoniaNotes`, label: "ammonia Notes", type: "input" },
      { name: `${p}.amylase`, label: "Amylase", type: "cross-option" },
      { name: `${p}.amylaseNotes`, label: "Amylase Notes", type: "input" },
      { name: `${p}.aspirin`, label: "aspirin", type: "cross-option" },
      { name: `${p}.aspirinNotes`, label: "aspirin Notes", type: "input" },
      { name: `${p}.ast`, label: "AST", type: "cross-option" },
      { name: `${p}.astNotes`, label: "AST Notes", type: "input" },
      { name: `${p}.alt`, label: "ALT", type: "cross-option" },
      { name: `${p}.altNotes`, label: "ALT Notes", type: "input" },
      { name: `${p}.bacteria`, label: "bacteria", type: "cross-option" },
      { name: `${p}.bacteriaNotes`, label: "bacteria Notes", type: "input" },
      { name: `${p}.bnp`, label: "BNP", type: "cross-option" },
      { name: `${p}.bnpNotes`, label: "BNP Notes", type: "input" },
      { name: `${p}.ck`, label: "CK", type: "cross-option" },
      { name: `${p}.ckNotes`, label: "CK Notes", type: "input" },
      { name: `${p}.ckmb`, label: "CKMB", type: "cross-option" },
      { name: `${p}.ckmbNotes`, label: "CKMB Notes", type: "input" },
      { name: `${p}.covid19naa`, label: "COVID-19 NAA", type: "cross-option" },
      {
        name: `${p}.covid19naaNotes`,
        label: "COVID-19 NAA Notes",
        type: "input",
      },
      { name: `${p}.cohb`, label: "COHb", type: "cross-option" },
      { name: `${p}.cohbNotes`, label: "COHb Notes", type: "input" },
      { name: `${p}.crp`, label: "CRP", type: "cross-option" },
      { name: `${p}.crpNotes`, label: "CRP Notes", type: "input" },
      { name: `${p}.ddimer`, label: "D-Dimer", type: "cross-option" },
      { name: `${p}.ddimerNotes`, label: "D-Dimer Notes", type: "input" },
      { name: `${p}.dfaIfa`, label: "DFA / IFA", type: "cross-option" },
      { name: `${p}.dfaIfaNotes`, label: "DFA / IFA Notes", type: "input" },
      { name: `${p}.dip`, label: "dip:", type: "cross-option" },
      { name: `${p}.dipInput`, label: "dip value", type: "input" },
      { name: `${p}.dipNotes`, label: "dip Notes", type: "input" },
      { name: `${p}.etoh`, label: "ETOH", type: "cross-option" },
      { name: `${p}.etohNotes`, label: "ETOH Notes", type: "input" },
      { name: `${p}.esr`, label: "ESR", type: "cross-option" },
      { name: `${p}.esrNotes`, label: "ESR Notes", type: "input" },
      { name: `${p}.fluScreen`, label: "Flu Screen", type: "cross-option" },
      { name: `${p}.fluScreenNotes`, label: "Flu Screen Notes", type: "input" },
      { name: `${p}.hcg`, label: "HCG", type: "cross-option" },
      { name: `${p}.serum`, label: "serum", type: "cross-option" },
      { name: `${p}.urine`, label: "urine", type: "cross-option" },
      {
        name: `${p}.hcgNotesSerumUrine`,
        label: "HCG serum/urine Notes",
        type: "input",
      },
      { name: `${p}.pos`, label: "pos", type: "cross-option" },
      { name: `${p}.neg`, label: "neg", type: "cross-option" },
      {
        name: `${p}.hcgNotesPosNeg`,
        label: "HCG pos/neg Notes",
        type: "input",
      },
      { name: `${p}.inr`, label: "INR", type: "cross-option" },
      { name: `${p}.inrNotes`, label: "INR Notes", type: "input" },
      { name: `${p}.lactate`, label: "Lactate", type: "cross-option" },
      { name: `${p}.lactateNotes`, label: "Lactate Notes", type: "input" },
      { name: `${p}.lipase`, label: "Lipase", type: "cross-option" },
      { name: `${p}.lipaseNotes`, label: "Lipase Notes", type: "input" },
      { name: `${p}.monoSpot`, label: "Mono Spot", type: "cross-option" },
      { name: `${p}.monoSpotNotes`, label: "Mono Spot Notes", type: "input" },
      { name: `${p}.nitrazine`, label: "Nitrazine", type: "cross-option" },
      { name: `${p}.nitrazineNotes`, label: "Nitrazine Notes", type: "input" },
      { name: `${p}.prot`, label: "prot", type: "cross-option" },
      { name: `${p}.protNotes`, label: "prot Notes", type: "input" },
      { name: `${p}.ptptt`, label: "PT / PTT", type: "cross-option" },
      { name: `${p}.ptpttNotes`, label: "PT / PTT Notes", type: "input" },
      { name: `${p}.rbc`, label: "RBC", type: "cross-option" },
      { name: `${p}.rbcNotes`, label: "RBC Notes", type: "input" },
      { name: `${p}.rh`, label: "Rh", type: "cross-option" },
      { name: `${p}.rhNotes`, label: "Rh Notes", type: "input" },
      { name: `${p}.ridt`, label: "RIDT", type: "cross-option" },
      { name: `${p}.ridtNotes`, label: "RIDT Notes", type: "input" },
      { name: `${p}.rma`, label: "RMA", type: "cross-option" },
      { name: `${p}.rmaNotes`, label: "RMA Notes", type: "input" },
      { name: `${p}.rotavirus`, label: "Rotavirus", type: "cross-option" },
      { name: `${p}.rotavirusNotes`, label: "Rotavirus Notes", type: "input" },
      { name: `${p}.rsv`, label: "RSV", type: "cross-option" },
      { name: `${p}.rsvNotes`, label: "RSV Notes", type: "input" },
      { name: `${p}.rtpcr`, label: "RT-PCR", type: "cross-option" },
      { name: `${p}.rtpcrNotes`, label: "RT-PCR Notes", type: "input" },
      { name: `${p}.strepScreen`, label: "Strep Screen", type: "cross-option" },
      {
        name: `${p}.strepScreenNotes`,
        label: "Strep Screen Notes",
        type: "input",
      },
      { name: `${p}.tox`, label: "TOX", type: "cross-option" },
      { name: `${p}.toxNotes`, label: "TOX Notes", type: "input" },
      { name: `${p}.troponin`, label: "Troponin", type: "cross-option" },
      { name: `${p}.troponinNotes`, label: "Troponin Notes", type: "input" },
      { name: `${p}.tsh`, label: "TSH", type: "cross-option" },
      { name: `${p}.tshNotes`, label: "TSH Notes", type: "input" },
      { name: `${p}.wbcThree`, label: "WBC (Other)", type: "cross-option" },
      { name: `${p}.wbcThreeNotes`, label: "WBC (Other) Notes", type: "input" },
      { name: `${p}.otherExtraOne`, label: "Other Extra 1", type: "input" },
      {
        name: `${p}.otherExtraOneNotes`,
        label: "Other Extra 1 Notes",
        type: "input",
      },
      { name: `${p}.otherExtraTwo`, label: "Other Extra 2", type: "input" },
      {
        name: `${p}.otherExtraTwoNotes`,
        label: "Other Extra 2 Notes",
        type: "input",
      },
      { name: `${p}.otherSwitch`, label: "Other Switch", type: "switch" },
      {
        name: `${p}.otherAllNormal`,
        label: "Other All Normal",
        type: "checkbox-button",
      },
      {
        name: `${p}.otherExcept`,
        label: "Other Except",
        type: "checkbox-button",
      },
      // Q005 - ABGs
      { name: `${p}.ra`, label: "RA/", type: "cross-option" },
      { name: `${p}.raInput`, label: "RA value", type: "input" },
      { name: `${p}.lo2`, label: "LO2", type: "cross-option" },
      { name: `${p}.raNotes`, label: "RA Notes", type: "input" },
      { name: `${p}.ph`, label: "pH", type: "cross-option" },
      { name: `${p}.phNotes`, label: "pH Notes", type: "input" },
      { name: `${p}.pCO2`, label: "pCO2", type: "cross-option" },
      { name: `${p}.pCO2Notes`, label: "pCO2 Notes", type: "input" },
      { name: `${p}.pO2`, label: "pO2", type: "cross-option" },
      { name: `${p}.pO2Notes`, label: "pO2 Notes", type: "input" },
      { name: `${p}.abgExtraOne`, label: "ABG Extra 1", type: "input" },
      {
        name: `${p}.abgExtraOneNotes`,
        label: "ABG Extra 1 Notes",
        type: "input",
      },
      { name: `${p}.abgExtraTwo`, label: "ABG Extra 2", type: "input" },
      {
        name: `${p}.abgExtraTwoNotes`,
        label: "ABG Extra 2 Notes",
        type: "input",
      },
      { name: `${p}.abgSwitch`, label: "ABGs Switch", type: "switch" },
      {
        name: `${p}.abgAllNormal`,
        label: "ABGs All Normal",
        type: "checkbox-button",
      },
      { name: `${p}.abgExcept`, label: "ABGs Except", type: "checkbox-button" },
      // Q006 - Cultures
      { name: `${p}.csBloodX`, label: "blood x", type: "cross-option" },
      { name: `${p}.csBloodXInput`, label: "blood x value", type: "input" },
      { name: `${p}.csBloodXNotes`, label: "blood x Notes", type: "input" },
      { name: `${p}.csSputum`, label: "sputum", type: "cross-option" },
      { name: `${p}.csSputumNotes`, label: "sputum Notes", type: "input" },
      { name: `${p}.urineNotes`, label: "urine Notes", type: "input" },
      { name: `${p}.csViral`, label: "viral", type: "cross-option" },
      { name: `${p}.csViralNotes`, label: "viral Notes", type: "input" },
      {
        name: `${p}.culturesExtraOne`,
        label: "Cultures Extra 1",
        type: "input",
      },
      {
        name: `${p}.culturesExtraOneNotes`,
        label: "Cultures Extra 1 Notes",
        type: "input",
      },
      { name: `${p}.culturesSwitch`, label: "Cultures Switch", type: "switch" },
      {
        name: `${p}.culturesAllNormal`,
        label: "Cultures All Normal",
        type: "checkbox-button",
      },
      {
        name: `${p}.culturesExcept`,
        label: "Cultures Except",
        type: "checkbox-button",
      },
      // Q007 - Peak Flow
      {
        name: `${p}.peakFlowInputOne`,
        label: "Peak Flow value 1",
        type: "input",
      },
      { name: `${p}.ml`, label: "ml", type: "cross-option" },
      {
        name: `${p}.preTreatment`,
        label: "pre treatment",
        type: "cross-option",
      },
      { name: `${p}.onArrival`, label: "on arrival", type: "cross-option" },
      {
        name: `${p}.peakFlowInputTwo`,
        label: "Peak Flow value 2",
        type: "input",
      },
      {
        name: `${p}.postTreatment`,
        label: "post treatment",
        type: "cross-option",
      },
      { name: `${p}.atDischarge`, label: "at discharge", type: "cross-option" },
      {
        name: `${p}.peakFlowInputThree`,
        label: "Peak Flow value 3",
        type: "input",
      },
      {
        name: `${p}.peakFlowSwitch`,
        label: "Peak Flow Switch",
        type: "switch",
      },
      {
        name: `${p}.peakFlowAllNormal`,
        label: "Peak Flow All Normal",
        type: "checkbox-button",
      },
      {
        name: `${p}.peakFlowExcept`,
        label: "Peak Flow Except",
        type: "checkbox-button",
      },
      // Q008 - Pulse Ox
      { name: `${p}.inputOne`, label: "Pulse Ox label 1", type: "input" },
      { name: `${p}.percentageInputOne`, label: "Pulse Ox % 1", type: "input" },
      { name: `${p}.raOne`, label: "% on RA / (1)", type: "cross-option" },
      { name: `${p}.raInputOne`, label: "RA value 1", type: "input" },
      { name: `${p}.l02One`, label: "L O2 (1)", type: "cross-option" },
      { name: `${p}.interpOne`, label: "Interp: (1)", type: "cross-option" },
      { name: `${p}.normalOne`, label: "normal (1)", type: "cross-option" },
      { name: `${p}.hypoxicOne`, label: "hypoxic (1)", type: "cross-option" },
      { name: `${p}.notesOne`, label: "Pulse Ox Notes 1", type: "input" },
      { name: `${p}.inputTwo`, label: "Pulse Ox label 2", type: "input" },
      { name: `${p}.percentageInputTwo`, label: "Pulse Ox % 2", type: "input" },
      { name: `${p}.raTwo`, label: "% on RA / (2)", type: "cross-option" },
      { name: `${p}.raInputTwo`, label: "RA value 2", type: "input" },
      { name: `${p}.l02Two`, label: "L O2 (2)", type: "cross-option" },
      { name: `${p}.interpTwo`, label: "Interp: (2)", type: "cross-option" },
      { name: `${p}.normalTwo`, label: "normal (2)", type: "cross-option" },
      { name: `${p}.hypoxicTwo`, label: "hypoxic (2)", type: "cross-option" },
      { name: `${p}.notesTwo`, label: "Pulse Ox Notes 2", type: "input" },
      { name: `${p}.inputThree`, label: "Pulse Ox label 3", type: "input" },
      {
        name: `${p}.percentageInputThree`,
        label: "Pulse Ox % 3",
        type: "input",
      },
      { name: `${p}.raThree`, label: "% on RA / (3)", type: "cross-option" },
      { name: `${p}.raInputThree`, label: "RA value 3", type: "input" },
      { name: `${p}.l02Three`, label: "L O2 (3)", type: "cross-option" },
      { name: `${p}.interpThree`, label: "Interp: (3)", type: "cross-option" },
      { name: `${p}.normalThree`, label: "normal (3)", type: "cross-option" },
      { name: `${p}.hypoxicThree`, label: "hypoxic (3)", type: "cross-option" },
      { name: `${p}.notesThree`, label: "Pulse Ox Notes 3", type: "input" },
      { name: `${p}.pulseOxSwitch`, label: "Pulse Ox Switch", type: "switch" },
      {
        name: `${p}.pulseOxAllNormal`,
        label: "Pulse Ox All Normal",
        type: "checkbox-button",
      },
      {
        name: `${p}.pulseOxExcept`,
        label: "Pulse Ox Except",
        type: "checkbox-button",
      },
    ]);
  });

  const { data: file } = useEncounterOrderFile(item.orderId);

  const toggleFileMode = React.useCallback(() => {
    setFileMode((prev) => !prev);
  }, []);

  const handleWbcExceptChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setValue(`items.${index}.wbcExcept`, e.target.checked);
      if (e.target.checked) {
        setValue(`items.${index}.wbcSwitch`, true);
      }
    },
    [index, setValue]
  );

  const handleChemistriesExceptChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setValue(`items.${index}.chemistriesExcept`, e.target.checked);
      if (e.target.checked) {
        setValue(`items.${index}.chemistriesSwitch`, true);
      }
    },
    [index, setValue]
  );

  const handleUaExceptChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setValue(`items.${index}.uaExcept`, e.target.checked);
      if (e.target.checked) {
        setValue(`items.${index}.uaSwitch`, true);
      }
    },
    [index, setValue]
  );

  const handleOtherExceptChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setValue(`items.${index}.otherExcept`, e.target.checked);
      if (e.target.checked) {
        setValue(`items.${index}.otherSwitch`, true);
      }
    },
    [index, setValue]
  );

  const handleAbgExceptChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setValue(`items.${index}.abgExcept`, e.target.checked);
      if (e.target.checked) {
        setValue(`items.${index}.abgSwitch`, true);
      }
    },
    [index, setValue]
  );

  const handleCulturesExceptChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setValue(`items.${index}.culturesExcept`, e.target.checked);
      if (e.target.checked) {
        setValue(`items.${index}.culturesSwitch`, true);
      }
    },
    [index, setValue]
  );

  const handlePeakFlowExceptChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setValue(`items.${index}.peakFlowExcept`, e.target.checked);
      if (e.target.checked) {
        setValue(`items.${index}.peakFlowSwitch`, true);
      }
    },
    [index, setValue]
  );

  const handlePulseOxExceptChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setValue(`items.${index}.pulseOxExcept`, e.target.checked);
      if (e.target.checked) {
        setValue(`items.${index}.pulseOxSwitch`, true);
      }
    },
    [index, setValue]
  );

  const wbcSwitch = watch(`items.${index}.wbcSwitch`);
  const chemistriesSwitch = watch(`items.${index}.chemistriesSwitch`);
  const uaSwitch = watch(`items.${index}.uaSwitch`);
  const otherSwitch = watch(`items.${index}.otherSwitch`);
  const abgSwitch = watch(`items.${index}.abgSwitch`);
  const culturesSwitch = watch(`items.${index}.culturesSwitch`);
  const peakFlowSwitch = watch(`items.${index}.peakFlowSwitch`);
  const pulseOxSwitch = watch(`items.${index}.pulseOxSwitch`);

  return (
    <Box mb="10px">
      <Card boxShadow="none">
        <Box {...GRID_PROPS}>
          <chakra.span display="flex" justifyContent="center">
            {isAuditorModifiedBy ? (
              <Box width="1.75rem" height="1.75rem" />
            ) : userDetails?.data?.fullName ? (
              <StaffAvatar
                profileUrl={userDetails.data.pictureUrl || ""}
                userName={userDetails.data.fullName}
                width="1.75rem"
                height="1.75rem"
              />
            ) : (
              <Avatar width="1.75rem" height="1.75rem" />
            )}
          </chakra.span>
          <chakra.span>
            <ChartRowElement
              isDisabled={isReadOnly}
              type="mask-date"
              name={`items.${index}.${LABS_DATE_TIME_QCODE}`}
              onClick={handleChangeUpdatedBy}
            />
          </chakra.span>
          <chakra.span textAlign="center">{item.order.orderId}</chakra.span>
          <chakra.span justifySelf="left">
            {renderOrderItemTime({
              scheduledTime: item.order.scheduledTime,
              renderType: "split",
            })}
          </chakra.span>
          <Icon
            as={EmrBed}
            fontSize="1.5rem"
            color={Boolean(item.order?.isObservation) ? "purple" : "gray.450"}
            sx={{
              position: "relative",
              left: "25px",
            }}
          />
          <chakra.span textAlign="left">{item.order.description}</chakra.span>
          <chakra.span textAlign="left">
            <IconButton
              aria-label="notes"
              icon={
                <Icon
                  as={EmrNotes}
                  color={item.order.notes ? "blue" : "gray.450"}
                  fontSize="1.25rem"
                />
              }
              disabled={!item.order?.notes}
              variant="icon"
              fontSize="1.4375rem"
              minWidth="unset"
              onClick={onToggle}
            />
          </chakra.span>
          <Icon
            as={EmrForm}
            fontSize="1.5rem"
            color={Boolean(file?.data) ? "green" : "gray.400"}
            onClick={isExpanded ? undefined : toggleExpand}
            cursor="pointer"
            pointerEvents="auto"
          />
          <Icon
            as={EmrEditCircle}
            fontSize="1.5rem"
            color={Boolean(hasInterpretation) ? "green" : "gray.400"}
            onClick={isExpanded ? undefined : toggleExpand}
            cursor="pointer"
            pointerEvents="auto"
          />
          <Badge
            bgColor={ORDER_STATUS_COLOR[item.order.status || "InProcess"]}
            fontWeight="700"
            fontSize="15px"
            color="white"
            borderRadius="20px"
            padding="0.5rem 0.5rem"
            textTransform="capitalize"
            width="100%"
            textAlign="center"
          >
            {enumMapper.toDisplay(
              "orderStatus",
              item.order.status || "InProcess"
            )}
          </Badge>
          <Icon
            as={SiChevronRight}
            w={6}
            h={6}
            fontSize="1.5rem"
            onClick={toggleExpand}
            cursor="pointer"
            pointerEvents="auto"
            color="blue"
            transform={!isExpanded ? "rotate(90deg)" : "rotate(270deg)"}
          />
        </Box>
      </Card>

      {isExpanded && (
        <Box
          sx={{
            margin: "5px",
          }}
        >
          <LabsChartFile
            file={file?.data}
            orderNumber={item.orderNumber}
            isFileMode={isFileMode}
            toggleFileMode={toggleFileMode}
          />

          <LabItemSignature
            index={index}
            useFormContext={useFormContext}
            isReadOnly={isReadOnly}
          />

          {!isFileMode && !hideInterpretations && (
            <>
              <ChartSubsection
                header={
                  <ChartSubsectionHeader
                    chartId={chartCode}
                    encounterId={encounterId}
                    sectionId={LABS_SECTION_CODE}
                    subsectionId="Q001"
                    interactions={getSubsectionInteractions("Q001")}
                    subsectionLabel="CBC"
                    subsectionNote="*Only select abnormalities"
                    marginTop="1rem"
                    showAuditsBtn={false}
                    subsectionOptions={
                      <>
                        <ChartRowElement
                          type="checkbox-button"
                          name={`items[${index}].wbcAllNormal`}
                          w="93px"
                        >
                          all normal
                        </ChartRowElement>
                        <ChartRowElement
                          type="checkbox-button"
                          name={`items[${index}].wbcExcept`}
                          onChange={handleWbcExceptChange}
                          w="93px"
                        >
                          except
                        </ChartRowElement>
                      </>
                    }
                    subsectionEndOptions={
                      <ChartRow>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="switch"
                            name={`items[${index}].wbcSwitch`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                      </ChartRow>
                    }
                  >
                    CBC
                  </ChartSubsectionHeader>
                }
                content={
                  wbcSwitch ? (
                    <ChartSubsectionContent>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="cross-option"
                            label="WBC"
                            name={`items[${index}].wbc`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].wbcNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="cross-option"
                            label="Hgb"
                            name={`items[${index}].hgb`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].hgbNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="cross-option"
                            label="Hct"
                            name={`items[${index}].hct`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].hctNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="cross-option"
                            label="Platelets"
                            name={`items[${index}].platelets`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].plateletsNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="cross-option"
                            label="segs"
                            name={`items[${index}].segs`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].segsNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="cross-option"
                            label="bands"
                            name={`items[${index}].bands`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].bandsNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="cross-option"
                            label="lymphs"
                            name={`items[${index}].lymphs`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].lymphsNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].cbcExtraOne`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].cbcExtraOneNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].cbcExtraTwo`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].cbcExtraTwoNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                    </ChartSubsectionContent>
                  ) : (
                    <></>
                  )
                }
              />
              <ChartSubsection
                header={
                  <ChartSubsectionHeader
                    chartId={chartCode}
                    encounterId={encounterId}
                    sectionId={LABS_SECTION_CODE}
                    subsectionId="Q002"
                    interactions={getSubsectionInteractions("Q002")}
                    subsectionLabel="Chemistries"
                    subsectionNote="*Only select abnormalities"
                    marginTop="1rem"
                    showAuditsBtn={false}
                    subsectionOptions={
                      <>
                        <ChartRowElement
                          type="checkbox-button"
                          name={`items[${index}].chemistriesAllNormal`}
                          w="93px"
                        >
                          all normal
                        </ChartRowElement>
                        <ChartRowElement
                          type="checkbox-button"
                          name={`items[${index}].chemistriesExcept`}
                          w="93px"
                          onChange={handleChemistriesExceptChange}
                        >
                          except
                        </ChartRowElement>
                      </>
                    }
                    subsectionEndOptions={
                      <ChartRow>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="switch"
                            name={`items[${index}].chemistriesSwitch`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                      </ChartRow>
                    }
                  >
                    Chemistries
                  </ChartSubsectionHeader>
                }
                content={
                  chemistriesSwitch ? (
                    <ChartSubsectionContent>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="cross-option"
                            label="NA"
                            name={`items[${index}].na`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].naNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="cross-option"
                            label="K"
                            name={`items[${index}].k`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].kNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="cross-option"
                            label="CO2"
                            name={`items[${index}].co2`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].co2Notes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="cross-option"
                            label="Gluc"
                            name={`items[${index}].gluc`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].glucNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="cross-option"
                            label="Bun"
                            name={`items[${index}].bun`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].bunNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="cross-option"
                            label="Creat"
                            name={`items[${index}].creat`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].creatNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].chemistriesExtraOne`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].chemistriesExtraOneNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].chemistriesExtraTwo`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].chemistriesExtraTwoNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                    </ChartSubsectionContent>
                  ) : (
                    <></>
                  )
                }
              />
              <ChartSubsection
                header={
                  <ChartSubsectionHeader
                    chartId={chartCode}
                    encounterId={encounterId}
                    sectionId={LABS_SECTION_CODE}
                    subsectionId="Q003"
                    interactions={getSubsectionInteractions("Q003")}
                    subsectionLabel="UA"
                    subsectionNote="*Only select abnormalities"
                    marginTop="1rem"
                    showAuditsBtn={false}
                    subsectionOptions={
                      <>
                        <ChartRowElement
                          type="checkbox-button"
                          name={`items[${index}].uaAllNormal`}
                          w="93px"
                        >
                          all normal
                        </ChartRowElement>
                        <ChartRowElement
                          type="checkbox-button"
                          name={`items[${index}].uaExcept`}
                          w="93px"
                          onChange={handleUaExceptChange}
                        >
                          except
                        </ChartRowElement>
                      </>
                    }
                    subsectionEndOptions={
                      <ChartRow>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="switch"
                            name={`items[${index}].uaSwitch`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                      </ChartRow>
                    }
                  >
                    UA
                  </ChartSubsectionHeader>
                }
                content={
                  uaSwitch ? (
                    <ChartSubsectionContent>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="cross-option"
                            label="WBC"
                            name={`items[${index}].uaWbc`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].uaWbcNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="cross-option"
                            label="RBC"
                            name={`items[${index}].uaRbc`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].uaRbcNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="cross-option"
                            label="bacteria"
                            name={`items[${index}].uaBacteria`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].uaBacteriaNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="cross-option"
                            label="dip"
                            name={`items[${index}].uaDip`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].uaDipNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].uaExtraOne`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].uaExtraOneNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].uaExtraTwo`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].uaExtraTwoNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                    </ChartSubsectionContent>
                  ) : (
                    <></>
                  )
                }
              />
              <ChartSubsection
                header={
                  <ChartSubsectionHeader
                    chartId={chartCode}
                    encounterId={encounterId}
                    sectionId={LABS_SECTION_CODE}
                    subsectionId="Q004"
                    interactions={getSubsectionInteractions("Q004")}
                    subsectionLabel="Other"
                    subsectionNote="*Only select abnormalities"
                    marginTop="1rem"
                    showAuditsBtn={false}
                    subsectionOptions={
                      <>
                        <ChartRowElement
                          type="checkbox-button"
                          name={`items[${index}].otherAllNormal`}
                          w="93px"
                        >
                          all normal
                        </ChartRowElement>
                        <ChartRowElement
                          type="checkbox-button"
                          name={`items[${index}].otherExcept`}
                          w="93px"
                          onChange={handleOtherExceptChange}
                        >
                          except
                        </ChartRowElement>
                      </>
                    }
                    subsectionEndOptions={
                      <ChartRow>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="switch"
                            name={`items[${index}].otherSwitch`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                      </ChartRow>
                    }
                  >
                    Other
                  </ChartSubsectionHeader>
                }
                content={
                  otherSwitch ? (
                    <ChartSubsectionContent>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="cross-option"
                            label="acetaminophen"
                            name={`items[${index}].acetaminophen`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].acetaminophenNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="cross-option"
                            label="Alk Phos"
                            name={`items[${index}].alkPhos`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].alkPhosNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="cross-option"
                            label="ammonia"
                            name={`items[${index}].ammonia`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].ammoniaNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="cross-option"
                            label="Amylase"
                            name={`items[${index}].amylase`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].amylaseNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="cross-option"
                            label="aspirin"
                            name={`items[${index}].aspirin`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].aspirinNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="cross-option"
                            label="AST"
                            name={`items[${index}].ast`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].astNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="cross-option"
                            label="ALT"
                            name={`items[${index}].alt`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].altNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="cross-option"
                            label="bacteria"
                            name={`items[${index}].bacteria`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].bacteriaNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="cross-option"
                            label="BNP"
                            name={`items[${index}].bnp`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].bnpNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="cross-option"
                            label="CK"
                            name={`items[${index}].ck`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].ckNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="cross-option"
                            label="CKMB"
                            name={`items[${index}].ckmb`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].ckmbNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="cross-option"
                            label="COVID-19 NAA"
                            name={`items[${index}].covid19naa`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].covid19naaNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="cross-option"
                            label="COHb"
                            name={`items[${index}].cohb`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].cohbNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="cross-option"
                            label="CRP"
                            name={`items[${index}].crp`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].crpNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="cross-option"
                            label="D-Dimer"
                            name={`items[${index}].ddimer`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].ddimerNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="cross-option"
                            label="DFA / IFA"
                            name={`items[${index}].dfaIfa`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].dfaIfaNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="cross-option"
                            label="dip:"
                            name={`items[${index}].dip`}
                          />
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].dipInput`}
                          />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].dipNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="cross-option"
                            label="ETOH"
                            name={`items[${index}].etoh`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].etohNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="cross-option"
                            label="ESR"
                            name={`items[${index}].esr`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].esrNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="cross-option"
                            label="Flu Screen"
                            name={`items[${index}].fluScreen`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].fluScreenNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="cross-option"
                            label="HCG"
                            name={`items[${index}].hcg`}
                          />
                          <ChartRowElement
                            type="cross-option"
                            label="serum"
                            name={`items[${index}].serum`}
                          />
                          <ChartRowElement
                            type="cross-option"
                            label="urine"
                            name={`items[${index}].urine`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].hcgNotesSerumUrine`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="cross-option"
                            label="HCG"
                            name={`items[${index}].hcg`}
                          />
                          <ChartRowElement
                            type="cross-option"
                            label="pos"
                            name={`items[${index}].pos`}
                          />
                          <ChartRowElement
                            type="cross-option"
                            label="neg"
                            name={`items[${index}].neg`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].hcgNotesPosNeg`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="cross-option"
                            label="INR"
                            name={`items[${index}].inr`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].inrNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="cross-option"
                            label="Lactate"
                            name={`items[${index}].lactate`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].lactateNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="cross-option"
                            label="Lipase"
                            name={`items[${index}].lipase`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].lipaseNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="cross-option"
                            label="Mono Spot"
                            name={`items[${index}].monoSpot`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].monoSpotNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="cross-option"
                            label="Nitrazine"
                            name={`items[${index}].nitrazine`}
                          />
                          <ChartRowElement
                            type="cross-option"
                            label="pos"
                            name={`items[${index}].pos`}
                          />
                          <ChartRowElement
                            type="cross-option"
                            label="neg"
                            name={`items[${index}].neg`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].nitrazineNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="cross-option"
                            label="prot"
                            name={`items[${index}].prot`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].protNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="cross-option"
                            label="PT / PTT"
                            name={`items[${index}].ptptt`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].ptpttNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="cross-option"
                            label="RBC"
                            name={`items[${index}].rbc`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].rbcNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="cross-option"
                            label="Rh"
                            name={`items[${index}].rh`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].rhNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="cross-option"
                            label="RIDT"
                            name={`items[${index}].ridt`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].ridtNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="cross-option"
                            label="RMA"
                            name={`items[${index}].rma`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].rmaNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="cross-option"
                            label="Rotavirus"
                            name={`items[${index}].rotavirus`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].rotavirusNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="cross-option"
                            label="RSV"
                            name={`items[${index}].rsv`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].rsvNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="cross-option"
                            label="RT-PCR"
                            name={`items[${index}].rtpcr`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].rtpcrNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="cross-option"
                            label="Strep Screen"
                            name={`items[${index}].strepScreen`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].strepScreenNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="cross-option"
                            label="TOX"
                            name={`items[${index}].tox`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].toxNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="cross-option"
                            label="Troponin"
                            name={`items[${index}].troponin`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].troponinNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="cross-option"
                            label="TSH"
                            name={`items[${index}].tsh`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].tshNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="cross-option"
                            label="WBC"
                            name={`items[${index}].wbcThree`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].wbcThreeNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].otherExtraOne`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].otherExtraOneNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].otherExtraTwo`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].otherExtraTwoNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                    </ChartSubsectionContent>
                  ) : (
                    <></>
                  )
                }
              />
              <ChartSubsection
                header={
                  <ChartSubsectionHeader
                    chartId={chartCode}
                    encounterId={encounterId}
                    sectionId={LABS_SECTION_CODE}
                    subsectionId="Q005"
                    interactions={getSubsectionInteractions("Q005")}
                    subsectionLabel="ABGs"
                    subsectionNote="*Only select abnormalities"
                    marginTop="1rem"
                    showAuditsBtn={false}
                    subsectionOptions={
                      <>
                        <ChartRowElement
                          type="checkbox-button"
                          name={`items[${index}].abgAllNormal`}
                          w="93px"
                        >
                          all normal
                        </ChartRowElement>
                        <ChartRowElement
                          type="checkbox-button"
                          name={`items[${index}].abgExcept`}
                          w="93px"
                          onChange={handleAbgExceptChange}
                        >
                          except
                        </ChartRowElement>
                      </>
                    }
                    subsectionEndOptions={
                      <ChartRow>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="switch"
                            name={`items[${index}].abgSwitch`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                      </ChartRow>
                    }
                  >
                    ABGs
                  </ChartSubsectionHeader>
                }
                content={
                  abgSwitch ? (
                    <ChartSubsectionContent>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="cross-option"
                            label="RA/"
                            name={`items[${index}].ra`}
                          />
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].raInput`}
                          />
                          <ChartRowElement
                            type="cross-option"
                            label="LO2"
                            name={`items[${index}].lo2`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].raNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="cross-option"
                            label="pH"
                            name={`items[${index}].ph`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].phNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="cross-option"
                            label="pCO2"
                            name={`items[${index}].pCO2`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].pCO2Notes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="cross-option"
                            label="pO2"
                            name={`items[${index}].pO2`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].pO2Notes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].abgExtraOne`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].abgExtraOneNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].abgExtraTwo`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].abgExtraTwoNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                    </ChartSubsectionContent>
                  ) : (
                    <></>
                  )
                }
              />
              <ChartSubsection
                header={
                  <ChartSubsectionHeader
                    chartId={chartCode}
                    encounterId={encounterId}
                    sectionId={LABS_SECTION_CODE}
                    subsectionId="Q006"
                    interactions={getSubsectionInteractions("Q006")}
                    subsectionLabel="Cultures"
                    subsectionNote="*Only select abnormalities"
                    marginTop="1rem"
                    showAuditsBtn={false}
                    subsectionOptions={
                      <>
                        <ChartRowElement
                          type="checkbox-button"
                          name={`items[${index}].culturesAllNormal`}
                          w="93px"
                        >
                          all normal
                        </ChartRowElement>
                        <ChartRowElement
                          type="checkbox-button"
                          name={`items[${index}].culturesExcept`}
                          w="93px"
                          onChange={handleCulturesExceptChange}
                        >
                          except
                        </ChartRowElement>
                      </>
                    }
                    subsectionEndOptions={
                      <ChartRow>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="switch"
                            name={`items[${index}].culturesSwitch`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                      </ChartRow>
                    }
                  >
                    Cultures
                  </ChartSubsectionHeader>
                }
                content={
                  culturesSwitch ? (
                    <ChartSubsectionContent>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="cross-option"
                            label="blood x"
                            name={`items[${index}].csBloodX`}
                          />
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].csBloodXInput`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].csBloodXNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="cross-option"
                            label="sputum"
                            name={`items[${index}].csSputum`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].csSputumNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="cross-option"
                            label="urine"
                            name={`items[${index}].urine`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].urineNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="cross-option"
                            label="viral"
                            name={`items[${index}].csViral`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].csViralNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].culturesExtraOne`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].culturesExtraOneNotes`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                    </ChartSubsectionContent>
                  ) : (
                    <></>
                  )
                }
              />
              <ChartSubsection
                header={
                  <ChartSubsectionHeader
                    chartId={chartCode}
                    encounterId={encounterId}
                    sectionId={LABS_SECTION_CODE}
                    subsectionId="Q007"
                    interactions={getSubsectionInteractions("Q007")}
                    subsectionLabel="Peak Flow"
                    subsectionNote="*Only select abnormalities"
                    marginTop="1rem"
                    showAuditsBtn={false}
                    subsectionOptions={
                      <>
                        <ChartRowElement
                          type="checkbox-button"
                          name={`items[${index}].peakFlowAllNormal`}
                          w="93px"
                        >
                          all normal
                        </ChartRowElement>
                        <ChartRowElement
                          type="checkbox-button"
                          name={`items[${index}].peakFlowExcept`}
                          w="93px"
                          onChange={handlePeakFlowExceptChange}
                        >
                          except
                        </ChartRowElement>
                      </>
                    }
                    subsectionEndOptions={
                      <ChartRow>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="switch"
                            name={`items[${index}].peakFlowSwitch`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                      </ChartRow>
                    }
                  >
                    Peak Flow
                  </ChartSubsectionHeader>
                }
                content={
                  peakFlowSwitch ? (
                    <ChartSubsectionContent>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].peakFlowInputOne`}
                            width="100px"
                          />
                          <ChartRowElement
                            type="cross-option"
                            label="ml"
                            name={`items[${index}].ml`}
                          />
                          <ChartRowElement
                            type="cross-option"
                            label="pre treatment"
                            name={`items[${index}].preTreatment`}
                          />
                          <ChartRowElement
                            type="cross-option"
                            label="on arrival"
                            name={`items[${index}].onArrival`}
                          />
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].peakFlowInputTwo`}
                            width="100px"
                          />
                          <ChartRowElement
                            type="cross-option"
                            label="ml"
                            name={`items[${index}].ml`}
                          />
                          <ChartRowElement
                            type="cross-option"
                            label="post treatment"
                            name={`items[${index}].postTreatment`}
                          />
                          <ChartRowElement
                            type="cross-option"
                            label="at discharge"
                            name={`items[${index}].atDischarge`}
                          />
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].peakFlowInputThree`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                    </ChartSubsectionContent>
                  ) : (
                    <></>
                  )
                }
              />
              <ChartSubsection
                header={
                  <ChartSubsectionHeader
                    chartId={chartCode}
                    encounterId={encounterId}
                    sectionId={LABS_SECTION_CODE}
                    subsectionId="Q008"
                    interactions={getSubsectionInteractions("Q008")}
                    subsectionLabel="Pulse Ox"
                    subsectionNote="*Only select abnormalities"
                    marginTop="1rem"
                    showAuditsBtn={false}
                    subsectionOptions={
                      <>
                        <ChartRowElement
                          type="checkbox-button"
                          name={`items[${index}].pulseOxAllNormal`}
                          w="93px"
                        >
                          all normal
                        </ChartRowElement>
                        <ChartRowElement
                          type="checkbox-button"
                          name={`items[${index}].pulseOxExcept`}
                          w="93px"
                          onChange={handlePulseOxExceptChange}
                        >
                          except
                        </ChartRowElement>
                      </>
                    }
                    subsectionEndOptions={
                      <ChartRow>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="switch"
                            name={`items[${index}].pulseOxSwitch`}
                          />
                          <ChartRowElement type="empty" />
                        </ChartRowGroup>
                      </ChartRow>
                    }
                  >
                    Pulse Ox
                  </ChartSubsectionHeader>
                }
                content={
                  pulseOxSwitch ? (
                    <ChartSubsectionContent>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].inputOne`}
                          />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].percentageInputOne`}
                            width="100px"
                          />
                          <ChartRowElement
                            type="cross-option"
                            label="% on RA / "
                            name={`items[${index}].raOne`}
                          />
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].raInputOne`}
                            width="100px"
                          />
                          <ChartRowElement
                            type="cross-option"
                            label="L O2"
                            name={`items[${index}].l02One`}
                          />
                          <ChartRowElement
                            type="cross-option"
                            label="Interp:"
                            name={`items[${index}].interpOne`}
                          />
                          <ChartRowElement
                            type="cross-option"
                            label="normal"
                            name={`items[${index}].normalOne`}
                          />
                          <ChartRowElement
                            type="cross-option"
                            label="hypoxic"
                            name={`items[${index}].hypoxicOne`}
                          />
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].notesOne`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].inputTwo`}
                          />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].percentageInputTwo`}
                            width="100px"
                          />
                          <ChartRowElement
                            type="cross-option"
                            label="% on RA / "
                            name={`items[${index}].raTwo`}
                          />
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].raInputTwo`}
                            width="100px"
                          />
                          <ChartRowElement
                            type="cross-option"
                            label="L O2"
                            name={`items[${index}].l02Two`}
                          />
                          <ChartRowElement
                            type="cross-option"
                            label="Interp:"
                            name={`items[${index}].interpTwo`}
                          />
                          <ChartRowElement
                            type="cross-option"
                            label="normal"
                            name={`items[${index}].normalTwo`}
                          />
                          <ChartRowElement
                            type="cross-option"
                            label="hypoxic"
                            name={`items[${index}].hypoxicTwo`}
                          />
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].notesTwo`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                      <ChartRow gridTemplateColumns={LABS_GRID_COLUMNS}>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].inputThree`}
                          />
                        </ChartRowGroup>
                        <ChartRowGroup>
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].percentageInputThree`}
                            width="100px"
                          />
                          <ChartRowElement
                            type="cross-option"
                            label="% on RA / "
                            name={`items[${index}].raThree`}
                          />
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].raInputThree`}
                            width="100px"
                          />
                          <ChartRowElement
                            type="cross-option"
                            label="L O2"
                            name={`items[${index}].l02Three`}
                          />
                          <ChartRowElement
                            type="cross-option"
                            label="Interp:"
                            name={`items[${index}].interpThree`}
                          />
                          <ChartRowElement
                            type="cross-option"
                            label="normal"
                            name={`items[${index}].normalThree`}
                          />
                          <ChartRowElement
                            type="cross-option"
                            label="hypoxic"
                            name={`items[${index}].hypoxicThree`}
                          />
                          <ChartRowElement
                            type="input"
                            name={`items[${index}].notesThree`}
                          />
                        </ChartRowGroup>
                      </ChartRow>
                    </ChartSubsectionContent>
                  ) : (
                    <></>
                  )
                }
              />
            </>
          )}
        </Box>
      )}
      <ModalOrderNotes
        isOpen={isOpen}
        onClose={onToggle}
        notes={item.order.notes}
      />
    </Box>
  );
});

type LabsChartProps = {
  hideInterpretations?: boolean;
} & BaseSharedChartProps;

const LabsChart = React.memo(function LabsChart(props: LabsChartProps) {
  const toast = useToast();
  const { chartCode, isReadOnly, hideInterpretations = false } = props;
  const {
    isPastEncounter,
    trackChartInteractions,
    isLoading: isTrackingLoading,
  } = useChartTrackingContext();

  const { encounterId = "" } = useParams<ChartRouteBaseParams>();
  const useFormContext = useForm<LabsChartForm>();
  const {
    handleSubmit,
    reset,
    getValues,
    formState: { isDirty },
  } = useFormContext;
  const lastSavedInteractionSnapshot = React.useRef<
    ChartFormSubsectionPayload[]
  >([]);

  const [filter, setFilter] = React.useState<OrderStatus | null>(null);

  const { data, isLoading } = useOrderChart({
    encounterId,
    chartCode,
    orderType: "Labs",
  });
  const { mutateAsync: updateChart, isLoading: isUpdating } =
    useUpdateOrderChart({
      encounterId,
      chartCode,
      orderType: "Labs",
    });

  const saveData = React.useCallback(
    async (values: LabsChartForm) => {
      const payload: UseOrderChartResponseItem[] = values.items.map((item) => {
        const { orderId, orderNumber, ...answers } = item;
        return {
          orderId,
          orderNumber,
          answers: mapFormObjectToAnswersList({ ...answers }),
        } as UseOrderChartResponseItem;
      });
      await updateChart(payload);
    },
    [updateChart]
  );

  const onSubmit: SubmitHandler<LabsChartForm> = React.useCallback(
    async (values) => {
      try {
        if (values.items) {
          const currentSnapshot = mapLabsItemsToSubsectionPayload(values.items);
          await trackChartInteractions(
            lastSavedInteractionSnapshot.current,
            currentSnapshot,
            {
              encounterId,
              chartCode,
              sectionId: LABS_SECTION_CODE,
              chartType: "shared",
            }
          );
          lastSavedInteractionSnapshot.current = currentSnapshot;
          await saveData(values);
        }
        // Reset form with latest values to set isDirty to false
        reset(values);
        toast({ description: formMessages.updateSuccess("Labs") });
      } catch (error) {
        toast({ description: extractApiErrorMessage(error) });
      }
    },
    [chartCode, encounterId, reset, saveData, toast, trackChartInteractions]
  );

  const handleSaveAndNext = useSaveAndNext(
    handleSubmit(onSubmit),
    "labs",
    "provider"
  );

  const onRouteChangeSave: FormPromptWithSaveProps["onSave"] = async (
    onComplete
  ) => {
    try {
      const values = getValues();
      await saveData(values);
      if (values.items) {
        lastSavedInteractionSnapshot.current = mapLabsItemsToSubsectionPayload(
          values.items
        );
      }
      toast({ description: formMessages.updateSuccess("Labs") });
      onComplete();
    } catch (error) {
      toast({ description: extractApiErrorMessage(error) });
    }
  };

  React.useEffect(() => {
    if (data) {
      const result = data.data.map((value) => {
        const mappedAnswers = mapAnswersListToFormObject(value.answers);
        return {
          orderId: value.orderId,
          order: value.order,
          interactions: value.interactions || [],
          // Initialize switches with default values
          wbcSwitch: Boolean(mappedAnswers.wbcSwitch) ?? false,
          chemistriesSwitch: Boolean(mappedAnswers.chemistriesSwitch) ?? false,
          uaSwitch: Boolean(mappedAnswers.uaSwitch) ?? false,
          otherSwitch: Boolean(mappedAnswers.otherSwitch) ?? false,
          abgSwitch: Boolean(mappedAnswers.abgSwitch) ?? false,
          culturesSwitch: Boolean(mappedAnswers.culturesSwitch) ?? false,
          peakFlowSwitch: Boolean(mappedAnswers.peakFlowSwitch) ?? false,
          pulseOxSwitch: Boolean(mappedAnswers.pulseOxSwitch) ?? false,
          // Initialize "except" checkboxes with default values
          wbcExcept: Boolean(mappedAnswers.wbcExcept) ?? false,
          chemistriesExcept: Boolean(mappedAnswers.chemistriesExcept) ?? false,
          uaExcept: Boolean(mappedAnswers.uaExcept) ?? false,
          otherExcept: Boolean(mappedAnswers.otherExcept) ?? false,
          abgExcept: Boolean(mappedAnswers.abgExcept) ?? false,
          culturesExcept: Boolean(mappedAnswers.culturesExcept) ?? false,
          peakFlowExcept: Boolean(mappedAnswers.peakFlowExcept) ?? false,
          pulseOxExcept: Boolean(mappedAnswers.pulseOxExcept) ?? false,
          // Initialize "all normal" checkboxes with default values
          wbcAllNormal: Boolean(mappedAnswers.wbcAllNormal) ?? false,
          chemistriesAllNormal:
            Boolean(mappedAnswers.chemistriesAllNormal) ?? false,
          uaAllNormal: Boolean(mappedAnswers.uaAllNormal) ?? false,
          otherAllNormal: Boolean(mappedAnswers.otherAllNormal) ?? false,
          abgAllNormal: Boolean(mappedAnswers.abgAllNormal) ?? false,
          culturesAllNormal: Boolean(mappedAnswers.culturesAllNormal) ?? false,
          peakFlowAllNormal: Boolean(mappedAnswers.peakFlowAllNormal) ?? false,
          pulseOxAllNormal: Boolean(mappedAnswers.pulseOxAllNormal) ?? false,
          ...mappedAnswers,
        };
      });
      reset({ items: result as unknown as LabsChartItem[] });
      lastSavedInteractionSnapshot.current =
        mapLabsItemsToSubsectionPayload(result);
    }
  }, [data, reset]);

  const filteredSubsections = React.useMemo(() => {
    if (!data?.data) {
      return [];
    }

    return data.data.filter((subsection) => {
      if (filter) {
        return subsection.order.status === filter;
      }
      return true;
    });
  }, [data, filter]);

  useHandlerBeforeSessionTimeout(async () => {
    if (isDirty) {
      await handleSubmit(onSubmit)();
    }
  });

  if (isLoading) {
    return <Loading />;
  }

  return (
    <FormProvider {...useFormContext}>
      <ChartForm
        onSubmit={handleSubmit(onSubmit)}
        onRouteChangeSave={onRouteChangeSave}
        isDirty={isDirty}
        isPromptSaveLoading={isUpdating || isTrackingLoading}
        disabled={isReadOnly}
      >
        <OrderChartSectionHeader
          icon={<Icon as={EmrLabs} />}
          chartId={chartCode}
          chartCode={chartCode}
          orderType="Labs"
          encounterId={encounterId}
          sectionId={LABS_SECTION_CODE}
          showNotesBtn
          showActionsBtns
          showAuditsBtns
          isLoading={isUpdating}
          isDisabled={isReadOnly}
          onSaveAndNext={handleSaveAndNext}
          sectionLabel="Labs"
        >
          Labs {isReadOnly ? "(Read Only)" : ""}
        </OrderChartSectionHeader>

        <LabsCounters
          filter={filter}
          setFilter={(newFilter) => setFilter(newFilter)}
          useFormContext={useFormContext}
        />

        <Box>
          <LabsHeaders />
          {filteredSubsections.map((row, index) => {
            return (
              <LabItem
                useFormContext={useFormContext}
                index={index}
                item={row}
                encounterId={encounterId}
                chartCode={chartCode}
                hideInterpretations={hideInterpretations}
                isReadOnly={isReadOnly || isPastEncounter}
              />
            );
          })}
        </Box>
      </ChartForm>
    </FormProvider>
  );
});

export { LabsChart };
